import React from "react";
import { Link } from "react-router-dom";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import PolicyLayout from "./PolicyLayout";

const Section = ({ id, number, title, children }) => (
  <section className="policy-section" id={id}>
    <p className="editorial-page__section-label">{number}</p>
    <h2>{title}</h2>
    {children}
  </section>
);

export const PaymentPolicy = () => (
  <PolicyLayout
    eyebrow="Checkout"
    title="Payment & billing policy"
    lede={`How payments, currency, authorizations, and billing issues are handled when you shop with ${siteConfig.brandName}.`}
    nav={[
      { href: "#methods", label: "Methods" },
      { href: "#charges", label: "Charges" },
      { href: "#security", label: "Security" },
      { href: "#issues", label: "Payment issues" },
    ]}
  >
    <Section id="methods" number="01" title="Currency and payment methods">
      <p>
        Prices and order totals are displayed and charged in United States
        dollars (USD). The payment methods actually available to you are those
        shown on the secure payment page and may vary by device, location, card
        issuer, and payment provider.
      </p>
      <p>
        Your bank or wallet provider may apply currency-conversion charges,
        foreign-transaction fees, or other charges under its own terms. Those
        charges are not collected by {siteConfig.brandName}.
      </p>
    </Section>
    <Section id="charges" number="02" title="Authorization and when you are charged">
      <p>
        Continuing from checkout transfers you to a third-party payment page.
        The provider may first authorize the amount and then capture it when the
        transaction is approved. An order is not accepted merely because an
        authorization appears on your account; acceptance occurs when we send
        an order confirmation.
      </p>
      <p>
        A declined or abandoned payment may leave a temporary pending
        authorization. Only the issuing bank can release that hold, and its
        release timing varies. We do not charge interest or impose payment-plan
        fees.
      </p>
    </Section>
    <Section id="security" number="03" title="Payment security and verification">
      <p>
        Card numbers and security codes are entered on the payment provider&apos;s
        hosted page, not on our checkout form. The provider and your issuer may
        use fraud screening or additional authentication. We may ask you to
        confirm order or billing information, and may refuse or cancel a
        transaction that cannot be verified.
      </p>
      <p>
        Do not send card numbers, security codes, passwords, or banking
        credentials to us by email or through the contact form.
      </p>
    </Section>
    <Section id="issues" number="04" title="Duplicate charges, refunds, and disputes">
      <p>
        If you believe you were charged incorrectly, email us before starting a
        payment dispute so we can investigate promptly. Include the order number,
        date, amount, and the last four digits of the payment card only. Approved
        refunds are returned to the original payment method and may take several
        business days to appear, depending on the provider or issuer.
      </p>
      <p>
        Nothing in this policy limits any non-excludable rights you have under
        applicable consumer or payment-services law. See our{" "}
        <Link to="/returns">return and refund policy</Link> for refund eligibility.
      </p>
    </Section>
  </PolicyLayout>
);

export const OrderPolicy = () => (
  <PolicyLayout
    eyebrow="Purchasing"
    title="Order & cancellation policy"
    lede="When an order becomes binding, what can be changed, and how cancellations and stock issues are handled."
    nav={[
      { href: "#placing", label: "Placing an order" },
      { href: "#acceptance", label: "Acceptance" },
      { href: "#changes", label: "Changes" },
      { href: "#cancellations", label: "Cancellations" },
    ]}
  >
    <Section id="placing" number="01" title="Placing an order">
      <p>
        Review product options, quantities, delivery details, and the total
        before payment. You are responsible for providing complete and accurate
        contact and delivery information. We are not responsible for delay or
        loss caused by an incorrect address supplied by the customer, although
        we will make reasonable efforts to help before dispatch.
      </p>
    </Section>
    <Section id="acceptance" number="02" title="Acknowledgment and acceptance">
      <p>
        A payment-provider screen or automated receipt acknowledges that we
        received your request; it does not by itself mean the order has been
        accepted. A contract is formed when we send an order confirmation after
        payment authorization and availability checks.
      </p>
      <p>
        We may reject or limit an order for stock shortages, obvious price or
        description errors, delivery restrictions, suspected fraud, reseller
        activity, or a legal requirement. If payment was captured for a rejected
        order, we will issue a refund to the original payment method.
      </p>
    </Section>
    <Section id="changes" number="03" title="Changing an order or address">
      <p>
        Email {siteConfig.contact.email} as soon as possible with the order
        number and requested change. We cannot guarantee changes after an order
        enters packing or a parcel is accepted by the carrier. We will never ask
        you to email payment-card details to make a change.
      </p>
    </Section>
    <Section id="cancellations" number="04" title="Customer and store cancellations">
      <p>
        You may request cancellation before dispatch. If the order has already
        shipped, follow the <Link to="/returns">return process</Link> after
        delivery. Personalized goods and work already begun at your request may
        not be cancellable except where applicable law requires otherwise.
      </p>
      <p>
        If we cancel, we will notify you using the email supplied at checkout.
        Any refund covers the cancelled item and related delivery amount charged
        for that item. Currency-conversion differences and issuer fees are
        controlled by your payment provider.
      </p>
    </Section>
    <Section id="limits" number="05" title="Quantity limits and resale">
      <p>
        Unless we agree otherwise in writing, products are offered for personal
        use. We may apply reasonable quantity limits or cancel orders that appear
        intended for unauthorized resale or that would prevent fair availability
        for other customers.
      </p>
    </Section>
  </PolicyLayout>
);

export const WarrantyPolicy = () => (
  <PolicyLayout
    eyebrow="After purchase"
    title="Warranty & product care policy"
    lede="Coverage for faults, limits of voluntary warranties, and practical care for stationery and art tools."
    nav={[
      { href: "#coverage", label: "Coverage" },
      { href: "#exclusions", label: "Exclusions" },
      { href: "#claims", label: "Claims" },
      { href: "#care", label: "Product care" },
    ]}
  >
    <Section id="coverage" number="01" title="Legal rights and stated warranties">
      <p>
        Products must match their description and any promises that cannot be
        excluded under applicable law. We do not add a separate store warranty
        unless a product page or written confirmation expressly says so. A
        manufacturer&apos;s warranty, where supplied, is provided by that
        manufacturer under its own terms and is additional to any mandatory
        consumer rights.
      </p>
    </Section>
    <Section id="exclusions" number="02" title="What is not a manufacturing fault">
      <ul className="policy-list">
        <li>Normal wear, ink depletion, lead breakage, or consumable use</li>
        <li>Damage from impact, moisture, heat, improper storage, or misuse</li>
        <li>Clogging or staining caused by incompatible ink, paint, or refills</li>
        <li>Unauthorized alterations, repairs, engraving, or disassembly</li>
        <li>Minor color, grain, texture, or finish variation disclosed as natural</li>
      </ul>
      <p>
        These examples do not exclude a remedy where the product was defective,
        unsafe, misdescribed, or otherwise protected by mandatory law.
      </p>
    </Section>
    <Section id="claims" number="03" title="Making a warranty or defect claim">
      <p>
        Contact us promptly with your order number, a description of the issue,
        when it began, and clear photographs or a short video where useful. Keep
        the product and packaging until we provide instructions. Depending on the
        circumstances and applicable law, the remedy may be repair, replacement,
        refund, or another appropriate solution.
      </p>
    </Section>
    <Section id="care" number="04" title="Basic care guidance">
      <ul className="policy-list">
        <li>Follow the maker&apos;s instructions and use compatible refills.</li>
        <li>Cap pens and markers after use and store liquids upright where directed.</li>
        <li>Keep paper, wood, metal, inks, and paints away from excess heat and moisture.</li>
        <li>Clean tools only with methods suitable for their material and finish.</li>
        <li>Keep sharp tools, small parts, inks, and adhesives away from children and pets.</li>
      </ul>
    </Section>
  </PolicyLayout>
);

export const ProductSafetyPolicy = () => (
  <PolicyLayout
    eyebrow="Use with care"
    title="Product safety & recall policy"
    lede="Safety information for art materials, cutting tools, small parts, and any product subject to corrective action."
    nav={[
      { href: "#safe-use", label: "Safe use" },
      { href: "#incidents", label: "Incidents" },
      { href: "#recalls", label: "Recalls" },
      { href: "#disposal", label: "Disposal" },
    ]}
  >
    <Section id="safe-use" number="01" title="Safe use and supervision">
      <p>
        Read all labels, warnings, age guidance, and maker instructions before
        use. Stationery and art supplies may contain sharp points, blades, small
        parts, pigments, solvents, adhesives, or liquids. Unless expressly
        described otherwise, products are not toys, food, cosmetics, or medical
        devices and should not be ingested or applied to the body.
      </p>
      <p>
        Use adequate ventilation where directed, protect work surfaces, and keep
        products away from heat, flame, children, and pets. An adult should
        supervise use by a child whenever the label or the nature of the product
        requires it.
      </p>
    </Section>
    <Section id="incidents" number="02" title="Damage or safety incidents">
      <p>
        Stop using a product if it is damaged, leaking, unusually hot, gives off
        an unexpected odor, or appears unsafe. For an immediate medical or fire
        emergency, contact the appropriate local emergency service or poison
        information service first. Then notify us with the order number, product
        name, batch or lot marking if present, and a factual description of what
        occurred.
      </p>
    </Section>
    <Section id="recalls" number="03" title="Recalls and safety notices">
      <p>
        If we learn that a supplied product is subject to a recall or safety
        correction, we may contact affected purchasers using order records and
        post instructions on this site. Follow the notice and do not sell, gift,
        repair, ship, or discard the affected product unless the notice tells you
        to do so. A recall remedy may include return, replacement, repair, or
        refund as required by the notice and applicable law.
      </p>
    </Section>
    <Section id="disposal" number="04" title="Storage, transport, and disposal">
      <p>
        Store and dispose of inks, paints, blades, adhesives, batteries, and
        other regulated materials according to the product label and local rules.
        Do not pour a product into drains or place it in ordinary waste when the
        label or local authority requires a special disposal route.
      </p>
    </Section>
  </PolicyLayout>
);

export const PromotionsPolicy = () => (
  <PolicyLayout
    eyebrow="Offers"
    title="Promotions & discount policy"
    lede="Rules for promotional prices, discount codes, gifts with purchase, and price adjustments."
    nav={[
      { href: "#eligibility", label: "Eligibility" },
      { href: "#codes", label: "Codes" },
      { href: "#returns", label: "Returns" },
      { href: "#corrections", label: "Corrections" },
    ]}
  >
    <Section id="eligibility" number="01" title="Offer terms and eligibility">
      <p>
        Each promotion is subject to the dates, products, destinations, minimum
        spend, customer eligibility, and other conditions stated with the offer.
        Unless stated otherwise, offers are for personal purchases, while stocks
        last, and have no cash value.
      </p>
    </Section>
    <Section id="codes" number="02" title="Discount codes and combinations">
      <p>
        Enter a code before completing payment. Codes are case-sensitive only
        where indicated and normally cannot be applied after purchase. One code
        may be used per order unless the offer expressly permits combination.
        Delivery charges, taxes, prior purchases, final-sale items, and selected
        products may be excluded.
      </p>
      <p>
        Codes may not be sold, copied from a private recipient, generated through
        automated means, or used through multiple identities to evade a limit.
      </p>
    </Section>
    <Section id="returns" number="03" title="Returns from promotional orders">
      <p>
        A refund is based on the amount actually paid for the returned item after
        allocating any order-level discount. If a return causes the remaining
        order to fall below a promotion threshold, the value of the lost discount
        or promotional gift may be deducted where the original offer clearly
        stated that condition and applicable law permits it.
      </p>
    </Section>
    <Section id="adjustments" number="04" title="Price adjustments">
      <p>
        We do not guarantee price matching or retrospective discounts. If an
        offer expressly includes a price-adjustment period, its published terms
        control. A later sale price does not make an earlier price inaccurate.
      </p>
    </Section>
    <Section id="corrections" number="05" title="Errors, misuse, and changes">
      <p>
        We may correct an obvious promotional error, refuse misuse, or end an
        offer when fraud, technical failure, legal requirements, or stock limits
        make continuation impracticable. We will not retroactively change a
        promotion for an order already accepted, except to remedy an error in the
        customer&apos;s favor or as required by law.
      </p>
    </Section>
  </PolicyLayout>
);

export const IntellectualPropertyPolicy = () => (
  <PolicyLayout
    eyebrow="Rights & attribution"
    title="Intellectual property policy"
    lede={`How ${siteConfig.brandName} protects original work, respects third-party rights, and handles infringement notices.`}
    nav={[
      { href: "#our-content", label: "Our content" },
      { href: "#third-party", label: "Third parties" },
      { href: "#permitted-use", label: "Permitted use" },
      { href: "#report", label: "Report an issue" },
    ]}
  >
    <Section id="our-content" number="01" title="Our original content">
      <p>
        The {siteConfig.brandName} name, original site text, page design,
        graphics, product presentation, and other original materials are owned by
        or licensed to the site operator and are protected by applicable
        intellectual-property laws. No ownership rights pass to you through use
        of the site or purchase of a product.
      </p>
    </Section>
    <Section id="third-party" number="02" title="Third-party names and materials">
      <p>
        Product makers retain rights in their respective names, marks, designs,
        packaging, and materials. A factual reference to a third-party brand is
        used only to identify or describe the relevant product or service and
        does not imply sponsorship, ownership, or affiliation unless we clearly
        state otherwise.
      </p>
      <p>
        We do not knowingly offer counterfeit products or intentionally publish
        material that infringes another person&apos;s rights.
      </p>
    </Section>
    <Section id="permitted-use" number="03" title="Permitted and prohibited use">
      <p>
        You may view the site and save or print a reasonable number of pages for
        personal, non-commercial shopping or record-keeping. Without written
        permission, you may not republish, sell, license, scrape for a commercial
        catalog, remove rights notices from, train a commercial content model on,
        or create confusingly similar branding from protected site content.
      </p>
      <p>
        These restrictions do not prevent uses permitted by applicable law, such
        as fair dealing, quotation, criticism, review, or another applicable
        exception.
      </p>
    </Section>
    <Section id="report" number="04" title="Reporting suspected infringement">
      <p>
        Email {siteConfig.contact.email} with: your name and contact details; an
        identification of the protected work, mark, or right; the exact site URL
        and material at issue; why you believe the use is unauthorized; and a
        statement that the information supplied is accurate. If you act for the
        rights holder, include the basis of your authority.
      </p>
      <p>
        We will review a sufficiently detailed notice, may request more
        information, and may remove or restrict material while the issue is
        assessed. Knowingly false or abusive notices may have legal consequences.
      </p>
    </Section>
  </PolicyLayout>
);

export const AcceptableUsePolicy = () => (
  <PolicyLayout
    eyebrow="Site rules"
    title="Acceptable use policy"
    lede="Rules that protect customers, site availability, content, and lawful use of the store."
    nav={[
      { href: "#allowed", label: "Allowed use" },
      { href: "#prohibited", label: "Prohibited use" },
      { href: "#automated", label: "Automated access" },
      { href: "#enforcement", label: "Enforcement" },
    ]}
  >
    <Section id="allowed" number="01" title="Lawful shopping and browsing">
      <p>
        You may browse, compare products, maintain a shopping bag or saved list,
        place genuine orders, and contact customer care for lawful personal or
        internal business purposes. Information you provide must be accurate and
        you must be authorized to use the payment method and contact details.
      </p>
    </Section>
    <Section id="prohibited" number="02" title="Prohibited conduct">
      <ul className="policy-list">
        <li>Introducing malware, harmful code, or unreasonable traffic</li>
        <li>Attempting to bypass security, access controls, or payment checks</li>
        <li>Testing for vulnerabilities without prior written authorization</li>
        <li>Impersonating another person or using stolen payment information</li>
        <li>Submitting unlawful, abusive, deceptive, or rights-infringing material</li>
        <li>Manipulating prices, inventory, reviews, offers, or checkout behavior</li>
        <li>Using the site to violate sanctions, export rules, or other applicable law</li>
      </ul>
    </Section>
    <Section id="automated" number="03" title="Automated access and indexing">
      <p>
        Ordinary search-engine indexing that respects our technical instructions
        is permitted. Other bots, scraping, bulk extraction, resale monitoring,
        or automated purchasing require prior written permission where they
        burden the service, reproduce protected content, or interfere with fair
        access to stock. Legally protected uses remain unaffected.
      </p>
    </Section>
    <Section id="enforcement" number="04" title="Protection and enforcement">
      <p>
        We may rate-limit, block, suspend, preserve relevant logs, cancel affected
        orders, or report activity when reasonably necessary to protect the site,
        customers, or others. A response will be proportionate to the conduct and
        does not limit remedies or obligations imposed by law.
      </p>
    </Section>
  </PolicyLayout>
);

export const AccessibilityStatement = () => (
  <PolicyLayout
    eyebrow="Inclusive access"
    title="Accessibility statement"
    lede={`Our commitment to making ${siteConfig.brandName} usable by people with different abilities, technologies, and ways of navigating.`}
    nav={[
      { href: "#commitment", label: "Commitment" },
      { href: "#features", label: "Features" },
      { href: "#feedback", label: "Feedback" },
      { href: "#assistance", label: "Assistance" },
    ]}
  >
    <Section id="commitment" number="01" title="Our approach">
      <p>
        We aim to provide clear structure, readable text, keyboard access,
        visible focus, meaningful labels, useful image alternatives, responsive
        layouts, and reduced-motion support where applicable. Accessibility is
        an ongoing practice, and parts of the site or third-party payment service
        may not yet work perfectly for every combination of browser and assistive
        technology.
      </p>
    </Section>
    <Section id="features" number="02" title="Using the site">
      <p>
        Current pages are designed to support zoom, reflow on smaller screens,
        keyboard navigation, form labels, error messages, and screen-reader
        landmarks. Browser settings or assistive technologies can be used to
        enlarge text, adjust color and contrast, or navigate by headings and
        links.
      </p>
    </Section>
    <Section id="feedback" number="03" title="Report an accessibility barrier">
      <p>
        Email {siteConfig.contact.email} with the page address, the task you were
        trying to complete, what happened, and—if you are comfortable sharing
        it—the browser and assistive technology used. Please do not include
        sensitive medical information or payment credentials.
      </p>
    </Section>
    <Section id="assistance" number="04" title="Alternative assistance">
      <p>
        If a barrier prevents you from obtaining product information or
        completing an order, ask us for the information in a reasonable
        alternative format or for assistance with the shopping process. We will
        respond {siteConfig.contact.responseTime} and work with you on a suitable
        option. We will never ask you to send full card details by email.
      </p>
    </Section>
  </PolicyLayout>
);

export const ComplaintsPolicy = () => (
  <PolicyLayout
    eyebrow="Customer care"
    title="Complaints & dispute resolution policy"
    lede="A fair process for raising a concern about an order, product, service, privacy matter, or accessibility barrier."
    nav={[
      { href: "#raise", label: "Raise a complaint" },
      { href: "#review", label: "Review" },
      { href: "#outcomes", label: "Outcomes" },
      { href: "#rights", label: "Your rights" },
    ]}
  >
    <Section id="raise" number="01" title="How to raise a complaint">
      <p>
        Email {siteConfig.contact.email} with your name, order number if relevant,
        a clear description of the concern, the outcome you are seeking, and any
        useful photographs or documents. Do not send full payment-card details,
        passwords, government identity documents, or unrelated personal data.
      </p>
    </Section>
    <Section id="review" number="02" title="Acknowledgment and review">
      <p>
        We normally acknowledge messages {siteConfig.contact.responseTime}. We
        aim to provide a substantive response within ten business days, although
        a complex carrier, manufacturer, payment, or privacy investigation may
        take longer. If so, we will explain the reason and provide an update.
      </p>
      <p>
        We review relevant records impartially, may ask focused follow-up
        questions, and give you a reasonable opportunity to clarify the concern.
      </p>
    </Section>
    <Section id="outcomes" number="03" title="Possible outcomes">
      <p>
        Depending on the facts and applicable policy or law, an outcome may
        include an explanation, correction, delivery assistance, repair,
        replacement, refund, removal of content, accessibility adjustment, or a
        decision that no further action is warranted. We will explain the main
        reason for our decision where reasonably possible.
      </p>
    </Section>
    <Section id="rights" number="04" title="Escalation and legal rights">
      <p>
        If you disagree with the response, reply with the specific point you want
        reconsidered and any new supporting information. This process does not
        require you to give up a non-excludable consumer right or prevent you
        from contacting an appropriate payment provider, regulator, consumer
        body, or court when applicable.
      </p>
    </Section>
  </PolicyLayout>
);

export const LegalNotice = () => (
  <PolicyLayout
    eyebrow="Site information"
    title="Legal notice & disclaimer"
    lede="Important information about the site operator, product content, third-party services, and legal limitations."
    nav={[
      { href: "#operator", label: "Operator" },
      { href: "#information", label: "Information" },
      { href: "#third-parties", label: "Third parties" },
      { href: "#limitations", label: "Limitations" },
    ]}
  >
    <Section id="operator" number="01" title="Site operator and contact">
      <p>
        This online store operates under the brand {siteConfig.brandName} from
        {" "}{siteConfig.businessLocation.countryShortName}. Its business address
        is {siteConfig.contact.address}, {siteConfig.businessLocation.countryName}.
        Customer-care and formal site notices may be sent to{" "}
        <a href={`mailto:${siteConfig.contact.email}`}>
          {siteConfig.contact.email}
        </a>
        . Publication of this business address does not imply a walk-in retail
        location, corporate registration, or professional accreditation.
      </p>
    </Section>
    <Section id="information" number="02" title="Product and site information">
      <p>
        We take reasonable care with product names, specifications, prices,
        availability, photographs, and delivery estimates. Screen settings,
        natural materials, manufacturing revisions, and manual errors can cause
        differences. Measurements are approximate unless expressly guaranteed.
        An obvious error may be corrected before an order is accepted.
      </p>
      <p>
        General stationery and art guidance on the site is informational and is
        not medical, safety, legal, or other professional advice. Follow product
        labels and obtain qualified advice where the intended use requires it.
      </p>
    </Section>
    <Section id="third-parties" number="03" title="Third-party services and rights">
      <p>
        Payment, delivery, and external links may involve independent third
        parties with their own terms and privacy practices. Their names and marks
        belong to their respective owners. A reference or link identifies the
        relevant service or product and does not imply an endorsement or
        affiliation unless clearly stated.
      </p>
    </Section>
    <Section id="limitations" number="04" title="Availability and legal limitations">
      <p>
        We do not promise uninterrupted or error-free access. Maintenance,
        security events, networks, carriers, or circumstances outside reasonable
        control may interrupt the site or delay an order. To the maximum extent
        permitted by law, we are not liable for indirect or consequential loss
        that was not reasonably foreseeable when the order was accepted.
      </p>
      <p>
        Nothing in this notice excludes liability or a remedy that cannot
        lawfully be excluded, including applicable rights relating to defective,
        unsafe, or misdescribed goods, fraud, or personal injury where the law
        prohibits exclusion.
      </p>
    </Section>
    <Section id="documents" number="05" title="Related policies and priority">
      <p>
        Our <Link to="/terms">terms of service</Link>,{" "}
        <Link to="/privacy">privacy policy</Link>, and the transaction-specific
        policies listed in the <Link to="/policies">policy center</Link> form the
        relevant store rules. If a product page or promotion contains specific
        terms, those specific terms apply to that product or promotion to the
        extent they do not conflict with mandatory law.
      </p>
    </Section>
  </PolicyLayout>
);

export const StoreCommitments = () => (
  <PolicyLayout
    eyebrow="At a glance"
    title="Customer commitments"
    lede="A concise summary of the standards that sit behind our detailed store policies."
    contact={false}
  >
    <Section number="01" title="Clear pricing">
      <p>
        Product prices and the flat tracked-delivery charge of{" "}
        {formatCurrency(siteConfig.flatShippingRate)} are shown before payment.
        We identify USD as the transaction currency and do not conceal mandatory
        store charges in a later step.
      </p>
    </Section>
    <Section number="02" title="Accurate descriptions">
      <p>
        We aim to describe products, availability, materials, options, and
        limitations accurately and correct confirmed errors promptly.
      </p>
    </Section>
    <Section number="03" title="Respect for people and rights">
      <p>
        We protect personal data, respect intellectual-property rights, provide a
        route to report accessibility barriers or safety concerns, and do not use
        a policy to remove rights that cannot lawfully be excluded.
      </p>
    </Section>
    <Section number="04" title="Reach us">
      <p>
        Questions and complaints can be sent to{" "}
        <a href={`mailto:${siteConfig.contact.email}`}>
          {siteConfig.contact.email}
        </a>
        . We normally reply {siteConfig.contact.responseTime}.
      </p>
    </Section>
  </PolicyLayout>
);
